// Copyright (c) Microsoft. All rights reserved.

import { BaseService } from './BaseService';

/**
 * Request payload for creating new feedback.
 */
export interface CreateFeedbackRequest {
    agentId: string;
    sessionId: string;
    messageId: string;
    rating: number;
    comment?: string;
}

/**
 * Request payload for updating existing feedback.
 */
export interface UpdateFeedbackRequest {
    rating?: number;
    comment?: string;
}

/**
 * Response structure for feedback operations.
 */
export interface FeedbackResponse {
    id: string;
    agentId: string;
    agentName: string;
    sessionId: string;
    messageId: string;
    rating: number;
    comment?: string;
    isRead: boolean;
    userId: string;
    userEmail: string;
    createdAt: string;
    updatedAt?: string;
}

/**
 * Service for managing feedback on agent responses.
 */
export class FeedbackService extends BaseService {
    private static readonly FEEDBACK_ENDPOINT = 'api/feedback';

    /**
     * Creates new feedback for an agent response.
     * @param request The feedback creation request.
     * @param accessToken The access token for authentication.
     * @returns The created feedback response.
     * @throws Error with status 409 if feedback already exists for the message.
     * @throws Error with status 400 if the request is invalid.
     */
    async createFeedback(request: CreateFeedbackRequest, accessToken?: string): Promise<FeedbackResponse> {
        return this.getResponseAsync<FeedbackResponse>(
            {
                commandPath: FeedbackService.FEEDBACK_ENDPOINT,
                method: 'POST',
                body: request,
            },
            accessToken,
        );
    }

    /**
     * Gets existing feedback for a message.
     * Used to check if feedback exists before showing the modal.
     * @param messageId The message ID to check.
     * @param accessToken The access token for authentication.
     * @returns The feedback if found, null if no feedback exists.
     */
    async getFeedbackByMessageId(messageId: string, accessToken?: string): Promise<FeedbackResponse | null> {
        try {
            return await this.getResponseAsync<FeedbackResponse>(
                {
                    commandPath: `${FeedbackService.FEEDBACK_ENDPOINT}/message/${messageId}`,
                    method: 'GET',
                },
                accessToken,
            );
        } catch (error: unknown) {
            // 404 means no feedback exists for this message
            const errorMessage = error instanceof Error ? error.message : String(error);
            if (errorMessage.includes('404')) {
                return null;
            }
            throw error;
        }
    }

    /**
     * Updates existing feedback.
     * @param id The feedback ID.
     * @param request The update request.
     * @param accessToken The access token for authentication.
     * @returns The updated feedback response.
     */
    async updateFeedback(id: string, request: UpdateFeedbackRequest, accessToken?: string): Promise<FeedbackResponse> {
        return this.getResponseAsync<FeedbackResponse>(
            {
                commandPath: `${FeedbackService.FEEDBACK_ENDPOINT}/${id}`,
                method: 'PUT',
                body: request,
            },
            accessToken,
        );
    }
}
